import React, { useState, useEffect } from "react";
import { usd, monthLabel, ROLES, DIVISIONS, StaticNote } from "./ui";
import { fetchFinancials } from "../api";
import "./Dashboard.css";

export default function YearToDate({ data, ctx }) {
  const [fin, setFin] = useState(null);
  const [open, setOpen] = useState({}); // monthKey -> bool

  useEffect(() => {
    fetchFinancials(ctx).then(setFin).catch(() => setFin({ breakdowns: {} }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { context, month, totals, byDivision } = data;
  const historical = context.historical || {};
  const budgets = context.budgets || {};
  const year = month.slice(0, 4);
  const keys = [];
  for (let m = 1; m <= 12; m++) keys.push(`${year}-${String(m).padStart(2, "0")}`);

  // Current-month breakdown derived from the live upload (Completed/Ongoing).
  const currentBreakdown = () => {
    const b = { Facility: {}, GEM: {} };
    DIVISIONS.forEach((d) => ROLES.forEach((r) => { b[d][r] = byDivision[d]?.[r]?.co || 0; }));
    b.facilityTotal = ROLES.reduce((s, r) => s + b.Facility[r], 0);
    b.gemTotal = ROLES.reduce((s, r) => s + b.GEM[r], 0);
    b.total = b.facilityTotal + b.gemTotal;
    return b;
  };

  const rows = keys.map((k) => {
    let value = null, type = "—", breakdown = null;
    const bd = fin?.breakdowns?.[k];
    if (k === month) { value = totals.completedOngoing; type = "Forecast"; breakdown = currentBreakdown(); }
    else if (historical[k] != null) { value = historical[k]; type = "Actual"; breakdown = bd?.actuals || null; }
    else if (budgets[k] != null) { value = budgets[k]; type = "Budget"; breakdown = bd?.budget || null; }
    return { k, value, type, breakdown };
  }).filter((r) => r.value != null);

  const ytdActual = rows.filter((r) => r.type === "Actual" || r.type === "Forecast").reduce((s, r) => s + r.value, 0);
  const bestActual = Math.max(0, ...rows.filter((r) => r.type === "Actual" || r.type === "Forecast").map((r) => r.value));
  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  return (
    <div className="dash">
      <section className="kpis kpis-3">
        <div className="kpi kpi-primary">
          <div className="kpi-label">YTD revenue (through {data.monthLabel})</div>
          <div className="kpi-value">{usd(ytdActual)}</div>
          <div className="kpi-sub">actuals + current forecast</div>
        </div>
        <div className="kpi"><div className="kpi-label">Months closed</div><div className="kpi-value">{rows.filter((r) => r.type === "Actual").length}</div></div>
        <div className="kpi"><div className="kpi-label">Best month (actual)</div><div className="kpi-value">{usd(bestActual)}</div></div>
      </section>

      <section className="panel">
        <div className="panel-head"><h2>{year} Monthly Revenue</h2><span>click a month to expand</span></div>
        <table className="grid">
          <thead><tr><th></th><th>Month</th><th>Revenue</th><th>Basis</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <React.Fragment key={r.k}>
                <tr className={"clickable " + (r.k === month ? "row-highlight" : "")} onClick={() => r.breakdown && toggle(r.k)}>
                  <td className="expander">{r.breakdown ? (open[r.k] ? "▾" : "▸") : ""}</td>
                  <td className="rowhead">{monthLabel(r.k)}</td>
                  <td className="strong">{usd(r.value)}</td>
                  <td><span className={"pill pill-" + r.type.toLowerCase()}>{r.type}</span></td>
                </tr>
                {open[r.k] && r.breakdown && (
                  <tr className="expand-row">
                    <td></td>
                    <td colSpan={3}>
                      <table className="grid nested">
                        <thead><tr><th>Division</th>{ROLES.map((x) => <th key={x}>{x}</th>)}<th>Total</th></tr></thead>
                        <tbody>
                          {DIVISIONS.map((div) => {
                            const b = r.breakdown[div] || {};
                            const tot = ROLES.reduce((s, x) => s + (b[x] || 0), 0);
                            return <tr key={div}><td className="rowhead">{div}</td>{ROLES.map((x) => <td key={x}>{usd(b[x] || 0)}</td>)}<td className="strong">{usd(tot)}</td></tr>;
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </section>

      <StaticNote>Closed-month actuals and their division/role breakdowns come from QuickBooks (ported); the current month is live from your upload; future months show budget. GEM RN has no separate historical line.</StaticNote>
    </div>
  );
}
